import React, { FC, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Divider,
  Card,
  TableContainer
} from '@mui/material';

import { useModalStore } from '../../../zustandStore/store.attributes';
import { API } from '../../../config/config';
import {
  ActionCell,
  DynamicTable
} from '../../../components/DynamicTable/DynamicTable';
import { useUpdateModalStore } from '../../../zustandStore/store.update';
import { descendingComparator } from '../../../Utils';
import { useAuth } from 'src/hooks/useAuth';
import LogoSpinner from 'src/components/LogoSpinner';
import { CreateBrandForm } from './CreateBrandForm';

interface RecentOrdersTableProps {
  className?: string;
}

const BrandsContent: FC<RecentOrdersTableProps> = () => {
  const { enhancedFetch } = useAuth();
  const [brandName, setBrandName] = useState('');
  const state = useModalStore((state) => state);
  const action = state.actions;
  const deleteState = useUpdateModalStore((state) => state);
  const [retry, setRetry] = useState(false);
  const [brands, setBrands] = useState([]);
  const [sortBy, SetSortBy] = useState<string>('id');
  const [sortAsc, setSortAsc] = useState<'asc' | 'desc'>('asc');
  const [loading, setLoading] = useState(false);

  const brandForm = CreateBrandForm({
    onSubmit: (data) => {
      setBrandName(data.brandName);
      setLoading(true);

      const isEdit = deleteState.settings.isEdit;
      const endpoint = isEdit
        ? `${API.HOST}/api/v1/brands/${deleteState.settings.payload.id}`
        : `${API.HOST}/api/v1/brands`;

      enhancedFetch(endpoint, {
        method: isEdit ? 'PUT' : 'POST',
        body: JSON.stringify({ brandName: data.brandName }),
        headers: {
          'Content-Type': 'application/json'
        }
      }).then((response) => {
        setLoading(false);
        setRetry(!retry); // Trigger a refresh of the brands list

        if (isEdit) {
          deleteState.actions.setModal({
            modalOpen: false,
            isEdit: false
          });
        } else {
          action.setModal({
            modalOpen: false
          });
        }
      }).catch((error) => {
        console.error(`Error ${isEdit ? 'updating' : 'creating'} brand:`, error);
        setLoading(false);
      });
    },
    data: {
      brandName
    },
    editData: deleteState.settings.payload,
    openModal: state.settings.modalOpen,
    editModal: deleteState.settings.isEdit,
    onClose: () => {
      if(deleteState.settings.isEdit) {
        deleteState.actions.setModal({
          modalOpen: false,
          isEdit: false
        });
      }else{
        state.actions.setModal({
          modalOpen: false,
        });
      }
    },
    isLoading: loading,
    title: deleteState.settings.isEdit ? 'Update Brand' : 'Add Brand',
    maxWidth: 'xl',
  })

  useEffect(() => {
    setLoading(true);
    enhancedFetch(`${API.HOST}/api/v1/brands`, {
      // 1. Change the URL
      method: 'GET'
    }).then((response) => {
      setLoading(false);
      if (Array.isArray(response)) {
        setBrands(
          response?.map((attribute: any) => {
            return {
              id: attribute.id, // Change the Key and Value to map the response
              name: attribute.name
            };
          })
        );
      } else {
        console.warn("Expected type Array, got", typeof response);
      }
    });
  }, [retry]);

  useEffect(() => {
    const _brands = brands.sort((a, b) => {
      if (sortAsc === 'asc') {
        return descendingComparator(a, b, sortBy);
      }
      if (sortAsc === 'desc') {
        return -descendingComparator(a, b, sortBy);
      }
    });
    setBrands(_brands);
  }, [sortBy, sortAsc]);

  if (loading)
    return (
      <div
        style={{
          height: '100vh',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center'
        }}
      >
        <LogoSpinner />
      </div>
    );

  return (
    <Card>

      {/* Add the modal here*/}
      {brandForm}
      <Divider />
      <TableContainer>
        <DynamicTable
          sortByField={sortBy}
          onSort={(headerProperty) => {
            SetSortBy(headerProperty);
            setSortAsc(sortAsc === 'asc' ? 'desc' : 'asc');
          }}
          sort={sortAsc}
          rows={brands?.map((brand: any) => ({
            id: brand.id,
            name: brand.name
          }))}

          headers={[

            {
              headerName: 'Name',
              field: 'name'
            },
            {
              headerName: 'Actions',
              field: 'actions',
              renderCell: (value: any) => {
                return (
                  <ActionCell
                    editEndpoint={`${API.HOST}/api/v1/brands/${value.row.id}`}
                    deleteEndpoint={`${API.HOST}/api/v1/brands/${value.row.id}`}
                    payload={value.row}
                    callback={() => setRetry(!retry)}
                  />
                );
              }
            }
          ]}
        />
      </TableContainer>
    </Card>
  );
};

BrandsContent.propTypes = {
  className: PropTypes.string
};
export default BrandsContent;
